import Dexie from 'dexie'
import { DirectReportAttendance } from '../../types/Employee/Attendance'
import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { ReworkTask, WorkItemTask } from '../../types/Work/WorkItemTask'
import { Employee } from '../../types/Employee/Employee'
import dayjs from 'dayjs'
import { getTransactionTables, trackEntityInsert } from '../../utils/MutationSnapshotUtils'

export const getDirectReportsAttendanceFB = async (
	db: ForemanDB,
	signOut?: boolean,
): Promise<DirectReportAttendance[]> => {
	if (signOut === true) {
		const attendance = await db.attendance
			.filter((a) => a.signInAt !== null && a.signOutAt === null && a.status === 'Present')
			.toArray()
		return attendance
	}
	const attendance = await db.attendance.toArray()
	return attendance
}
export const signInWorkersBulkFB = async (
	db: ForemanDB,
	attendance: DirectReportAttendance[],
	consentForms?: { workerId: number; blob: Blob }[],
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		db.attendance,
		db.consentForms,
		db.mutationSnapshot,
		db.mutations,
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const workerIds = attendance.map((a) => a.workerId)
			const dbAttendance: DirectReportAttendance[] = await tx
				.table('attendance')
				.filter((a) => workerIds.includes(a.workerId))
				.toArray()
			for (const a of dbAttendance) {
				const workerAttendance = attendance.find((att) => att.workerId === a.workerId)
				if (!workerAttendance) {
					continue
				}
				if (workerAttendance.signOutAt === null) {
					await tx.table('attendance').update(a.workerId, {
						signInAt: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
						status: workerAttendance.status,
						areaId: workerAttendance.areaId,
						areaName: workerAttendance.areaName,
						formSigned: consentForms?.find((c) => c.workerId === a.workerId) !== undefined,
						suggestedAreaId: workerAttendance.suggestedAreaId,
						date: dayjs(new Date().toLocaleDateString()).toDate(),
					})
					await tx.table('consentForms').put({
						workerId: a.workerId,
						blob: consentForms?.find((c) => c.workerId === a.workerId)?.blob,
						type: 'DayStartConsent',
					})
				}
			}

			await tx.table('mutations').put({
				route: '/Attendance/SignIn/Bulk',
				method: 'POST',
				body: attendance,
				title: `Signed ${attendance.length} workers in`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const signOutWorkersBulkFB = async (
	db: ForemanDB,
	workerIds: number[],
	consentForms?: { workerId: number; blob: Blob }[],
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'attendance',
			'consentForms',
			'directReports',
			'tasks',
			'reworks',
			'workItems',
			'constraints',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const dbAttendance: DirectReportAttendance[] = await tx
				.table('attendance')
				.where('workerId')
				.anyOf(workerIds)
				.toArray()
			for (const a of dbAttendance) {
				if (a.signOutAt === null) {
					await tx.table('attendance').update(a.workerId, {
						signOutAt: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
					})
				}
				await tx.table('consentForms').put({
					workerId: a.workerId,
					blob: consentForms?.find((c) => c.workerId === a.workerId)?.blob,
					type: 'DayEndConsent',
				})
			}
			const directReports = await tx.table('directReports').where('id').anyOf(workerIds).toArray()
			const tasksToDeactivate = new Set(
				directReports.map((d: Employee) => d.activeTaskId).filter((t) => t !== undefined),
			)
			const reworksToDeactivate = new Set(
				directReports.map((d: Employee) => d.activeReworkId).filter((r) => r !== undefined),
			)
			for (const t of directReports) {
				await tx.table('directReports').update(t.id, {
					activeTaskId: undefined,
					activeReworkId: undefined,
				})
			}
			for (const t of Array.from(tasksToDeactivate)) {
				if (t) {
					await tx.table('tasks').update(t, {
						status: 'Inactive',
					})
				}
			}
			for (const r of Array.from(reworksToDeactivate)) {
				if (r) {
					await tx.table('reworks').update(r, {
						status: 'Inactive',
					})
				}
			}
			const workItemsToUpdate = (
				await tx
					.table('tasks')
					.filter((t: WorkItemTask) => tasksToDeactivate.has(t.id))
					.toArray()
			)
				.map((t: WorkItemTask) => t.workItemId)
				.concat(
					(
						await tx
							.table('reworks')
							.filter((r: ReworkTask) => reworksToDeactivate.has(r.id))
							.toArray()
					).map((r: ReworkTask) => r.workItemId),
				)

			for (const w of Array.from(new Set(workItemsToUpdate)).filter((w) => w !== undefined)) {
				await db.triggerWorkItemStatusUpdate(w!, tx)
			}
			await tx.table('mutations').put({
				route: '/Attendance/SignOut/Bulk',
				method: 'PATCH',
				body: workerIds,
				title: `Signed out ${workerIds.length} workers`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const revertSignOutWorkersFB = async (db: ForemanDB, workerId: number) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, ['attendance', 'consentForms']),
		async () => {
			const tx = Dexie.currentTransaction

			await tx.table('attendance').update(workerId, {
				signOutAt: null,
				status: 'Present',
			})
		},
	)
}
